type Snapshot = { pageViewId: string; revision: number; startedAtUtc: string };
type Entry = { body: string; pageViewId: string; revision: number; expires: number; attempted: boolean };
const lifetime = 86400000;
const maxBytes = 256 * 1024;

// One outbox per endpoint/document, reused if a React effect is mounted again.
const instances = new Map<string, ReturnType<typeof createInsightOutbox>>();
export function getInsightOutbox(endpoint: string, headers: Record<string, string>, rejected: () => void) {
  let outbox = instances.get(endpoint);
  if (!outbox) { outbox = createInsightOutbox(endpoint, headers, rejected); instances.set(endpoint, outbox); }
  return outbox;
}

export function createInsightOutbox(endpoint: string, headers: Record<string, string>, rejected: () => void) {
  const key = 'pte_home_insights_outbox_v1:' + endpoint;
  let queue: Entry[] = [], failures = 0, retryAt = 0, timer: number | undefined;
  const active = new Set<Entry>();
  const revisions = new Map<string, number>(), invalid = new Set<string>();
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) || '[]');
    if (Array.isArray(saved)) queue = saved.filter((e: Entry) => {
      if (!e || typeof e.body !== 'string' || e.body.length > 32768 || !Number.isFinite(e.expires) || e.expires <= Date.now()) return false;
      try { const body = JSON.parse(e.body); return body.pageViewId === e.pageViewId && body.revision === e.revision
        && Number.isInteger(e.revision) && e.revision > 0 && e.expires === Date.parse(body.startedAtUtc) + lifetime; } catch { return false; }
    }).slice(-16);
  } catch { /* Storage is optional. */ }
  for (const e of queue) revisions.set(e.pageViewId, Math.max(revisions.get(e.pageViewId) || 0, e.revision));
  function save() {
    queue = queue.filter(e => e.expires > Date.now());
    while (queue.length > 16 || JSON.stringify(queue).length > maxBytes) queue.shift();
    try { sessionStorage.setItem(key, JSON.stringify(queue)); } catch { /* Keep the in-memory outbox. */ }
  }
  function schedule() {
    window.clearTimeout(timer);
    if (queue.length) timer = window.setTimeout(() => void flush(), Math.max(0, retryAt - Date.now()));
  }
  async function flush(exiting = false) {
    save(); if (!queue.length) return;
    if (Date.now() < retryAt) { schedule(); return; }
    const entry = exiting ? queue[queue.length - 1] : queue[0];
    // A final cumulative revision can use a second keepalive request while the
    // earlier exact attempt is in flight. Ordinary sends stay serial.
    if (active.has(entry) || active.size >= (exiting ? 2 : 1)) return;
    active.add(entry); entry.attempted = true; save();
    let delay = 0;
    try {
      const response = await fetch(endpoint, { method: 'POST', keepalive: true, credentials: 'omit', headers,
        body: entry.body, signal: AbortSignal.timeout(5000) });
      let throttled = response.status === 429;
      if (response.status === 400) {
        try {
          const body = await response.json();
          throttled = typeof body === 'object' && body !== null
            && (('errorMessage' in body && body.errorMessage === 'TooManyRequests')
              || ('ErrorMessage' in body && body.ErrorMessage === 'TooManyRequests'));
        } catch { /* Ordinary validation response. */ }
      }
      if (response.ok) { queue = queue.filter(e => e !== entry); failures = 0; }
      else if (!throttled && response.status >= 400 && response.status < 500 && response.status !== 408) {
        invalid.add(entry.pageViewId); queue = queue.filter(e => e.pageViewId !== entry.pageViewId);
        console.warn('PTE homepage insight rejected', response.status); rejected(); failures = 0;
      } else {
        failures++;
        const value = response.headers?.get('Retry-After');
        if (value) delay = /^\d+$/.test(value) ? Number(value) * 1000 : Date.parse(value) - Date.now();
      }
    } catch { failures++; }
    finally {
      active.delete(entry); save();
      // A concurrent successful exit response must not shorten Retry-After
      // already supplied by the other request.
      retryAt = Math.max(retryAt, Date.now() + Math.max(Number.isFinite(delay) ? Math.min(lifetime, delay) : 0,
        failures ? Math.min(30000, 1000 * 2 ** Math.min(failures, 5)) : 0));
      schedule();
    }
  }
  function enqueue(snapshot: Omit<Snapshot, 'revision'> & Record<string, unknown>, exiting = false) {
    if (invalid.has(snapshot.pageViewId)) return;
    const revision = (revisions.get(snapshot.pageViewId) || 0) + 1;
    revisions.set(snapshot.pageViewId, revision);
    const entry = { pageViewId: snapshot.pageViewId, revision, expires: Date.parse(snapshot.startedAtUtc) + lifetime,
      body: JSON.stringify({ ...snapshot, revision }), attempted: false };
    // Never alter an attempted revision. Replace only an unsent cumulative tail.
    queue = queue.filter(e => e.pageViewId !== snapshot.pageViewId || e.attempted);
    queue.push(entry); save(); void flush(exiting);
  }
  window.addEventListener('online', () => void flush());
  window.addEventListener('pageshow', () => void flush());
  save(); void flush();
  return { enqueue, flush };
}
